import React, { Suspense, lazy } from 'react';
import {Routes, Route, useLocation, useParams, Navigate} from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { RegionProvider } from './context/RegionContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Cart from './components/cart/Cart';
import ScrollToTop from './components/common/ScrollToTop';
import WhatsAppButton from './components/common/WhatsAppButton';
import SEO from './components/seo/SEO';
import { getCityLocation } from './utils/locationCatalog';

// Route-level code splitting: each page loads on demand instead of one big bundle.
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const StoreLocations = lazy(() => import('./pages/StoreLocations'));
const ShippingInfo = lazy(() => import('./pages/ShippingInfo'));
const ReturnsExchanges = lazy(() => import('./pages/ReturnsExchanges'));
const FAQ = lazy(() => import('./pages/FAQ'));
const WarrantyInfo = lazy(() => import('./pages/WarrantyInfo'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const LocalProductDashboard = lazy(() => import('./pages/LocalProductDashboard'));
const LocationShopPage = lazy(() => import('./pages/LocationShopPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const PageLoader = () => (
  <div className="flex items-center justify-center py-24" aria-label="Loading page">
    <div className="w-10 h-10 border-4 border-gray-200 border-t-dark-900 rounded-full animate-spin" />
  </div>
);

// Canonical product URL is /products/:productId — redirect the legacy singular form.
const LegacyProductRedirect = () => {
  const { productId } = useParams();
  return <Navigate to={`/products/${productId}`} replace />;
};

// Canonical city URL is /<provinceId>/:citySlug — redirect the legacy /shop/city form,
// resolving the city to its real province (e.g. /shop/city/montreal -> /quebec/montreal).
const LegacyCityRedirect = () => {
  const { citySlug } = useParams();
  const provinceId = getCityLocation(citySlug)?.provinceId || 'ontario';
  return <Navigate to={`/${provinceId}/${citySlug}`} replace />;
};

// Canonical province hub is /<provinceId> — redirect legacy province URL forms.
const LegacyProvinceRedirect = () => {
  const { provinceId } = useParams();
  return <Navigate to={`/${provinceId}`} replace />;
};

// /ontario/:citySlug is only canonical for Ontario cities — a non-Ontario city
// slug (e.g. /ontario/montreal) redirects to its real province URL.
const OntarioCityGate = () => {
  const { citySlug } = useParams();
  const cityLocation = getCityLocation(citySlug);
  if (cityLocation && cityLocation.provinceId !== 'ontario') {
    return <Navigate to={`/${cityLocation.provinceId}/${citySlug}`} replace />;
  }
  return <LocationShopPage scope="city" fixedProvinceId="ontario" />;
};

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin/');

  return (
    <RegionProvider>
      <CartProvider>
        <ScrollToTop />
        
        <div className="App min-h-screen flex flex-col">
          <SEO />
          {!isAdminRoute && <Header />}
          <div className="flex-1">
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/product/:productId" element={<LegacyProductRedirect />} />
              <Route path="/products/:productId" element={<ProductDetailPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmationPage />} />
              <Route path="/locations" element={<StoreLocations />} />
              <Route path="/delivery-areas" element={<Navigate to="/locations" replace />} />
              <Route path="/shipping" element={<ShippingInfo />} />
              <Route path="/returns" element={<ReturnsExchanges />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/warranty" element={<WarrantyInfo />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path='/blog' element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/province/:provinceId" element={<LegacyProvinceRedirect />} />
              <Route path="/ontario" element={<LocationShopPage scope="province" fixedProvinceId="ontario" />} />
              <Route path="/ontario/:citySlug" element={<OntarioCityGate />} />
              <Route path="/alberta" element={<LocationShopPage scope="province" fixedProvinceId="alberta" />} />
              <Route path="/alberta/:citySlug" element={<LocationShopPage scope="city" fixedProvinceId="alberta" />} />
              <Route path="/british-columbia" element={<LocationShopPage scope="province" fixedProvinceId="british-columbia" />} />
              <Route path="/british-columbia/:citySlug" element={<LocationShopPage scope="city" fixedProvinceId="british-columbia" />} />
              <Route path="/quebec" element={<LocationShopPage scope="province" fixedProvinceId="quebec" />} />
              <Route path="/quebec/:citySlug" element={<LocationShopPage scope="city" fixedProvinceId="quebec" />} />
              <Route path="/categories/:categorySlug/:subcategorySlug" element={<ProductsPage />} />
              <Route path="/categories/:categorySlug" element={<ProductsPage />} />
              {/* Canonical province hub is /<provinceId> — redirect legacy province forms */}
              <Route path="/shop/province/ontario" element={<Navigate to="/ontario" replace />} />
              <Route path="/province/ontario" element={<Navigate to="/ontario" replace />} />
              <Route path="/shop/province/:provinceId" element={<LegacyProvinceRedirect />} />
              {/* Canonical city URL is /<provinceId>/:citySlug — redirect the legacy form */}
              <Route path="/shop/city/:citySlug" element={<LegacyCityRedirect />} />
              <Route path="/admin/products" element={<LocalProductDashboard />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </div>

          {!isAdminRoute && <Footer />}
          {!isAdminRoute && <Cart />}
          {!isAdminRoute && <WhatsAppButton />}
        </div>
      </CartProvider>
    </RegionProvider>
  );
}

export default App;
